import Icon from '@/components/ui/AppIcon';

interface Service {
  title: string;
  description: string;
  icon: string;
  features: string[];
  color: string;
}

const ServicesGrid = () => {
  const services: Service[] = [
    {
      title: 'Campaign Orchestration',
      description: 'Coordinate multi-channel campaigns with intelligent timing, sequencing, and personalization across all touchpoints.',
      icon: 'CommandLineIcon',
      features: [
        'Multi-channel coordination',
        'Automated A/B testing',
        'Dynamic content optimization',
        'Real-time performance tracking'
      ],
      color: 'bg-accent'
    },
    {
      title: 'Lead Nurturing',
      description: 'Automated drip campaigns that adapt to prospect behavior, moving leads through the funnel with personalized messaging.',
      icon: 'ArrowPathIcon',
      features: [
        'Behavioral trigger automation',
        'Progressive profiling',
        'Lead scoring integration',
        'Conversion path optimization'
      ],
      color: 'bg-success'
    },
    {
      title: 'Email Marketing AI',
      description: 'Intelligent email campaigns with predictive send-time optimization, dynamic content, and automated segmentation.',
      icon: 'EnvelopeIcon',
      features: [
        'Predictive send-time optimization',
        'Dynamic content blocks',
        'Automated list segmentation',
        'Deliverability optimization'
      ],
      color: 'bg-primary'
    },
    {
      title: 'Social Media Automation',
      description: 'Schedule, publish, and optimize social content across platforms with AI-driven engagement and audience insights.',
      icon: 'ShareIcon',
      features: [
        'Multi-platform scheduling',
        'Content performance analytics',
        'Audience engagement tracking',
        'Hashtag optimization'
      ],
      color: 'bg-accent'
    },
    {
      title: 'Marketing Analytics',
      description: 'Comprehensive dashboards with AI-powered insights, attribution modeling, and predictive performance forecasting.',
      icon: 'ChartBarIcon',
      features: [
        'Multi-touch attribution',
        'Predictive analytics',
        'Custom reporting dashboards',
        'ROI tracking and optimization'
      ],
      color: 'bg-success'
    },
    {
      title: 'Content Personalization',
      description: 'Dynamic website and email content that adapts to visitor behavior, preferences, and journey stage in real-time.',
      icon: 'SparklesIcon',
      features: [
        'Real-time content adaptation',
        'Behavioral targeting',
        'Personalized recommendations',
        'Dynamic landing pages'
      ],
      color: 'bg-primary'
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-muted px-4 py-2 rounded-full mb-4">
            <Icon name="RocketLaunchIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">Comprehensive Solutions</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Complete Marketing Automation Suite
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Every tool you need to transform your marketing operations into a high-performing, data-driven revenue engine.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-card rounded-2xl p-8 shadow-elevated border border-border hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className={`w-16 h-16 ${service.color} rounded-xl flex items-center justify-center mb-6`}>
                <Icon name={service.icon as any} size={32} className="text-white" />
              </div>
              
              <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                {service.title}
              </h3>
              
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                {service.description}
              </p>
              
              <div className="space-y-3">
                {service.features.map((feature, idx) => (
                  <div key={idx} className="flex items-start space-x-2">
                    <Icon name="CheckCircleIcon" size={18} className="text-success flex-shrink-0 mt-0.5" variant="solid" />
                    <span className="text-sm text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;